// src/views/whatsappInbox/utils/MediaGroup.tsx
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { 
  cacheMedia, 
  openMediaPreview 
} from '../../../store/slices/mediaSlice';
import { MessageMedia } from '../components/MessageMedia';
import { 
  getGridConfig, 
  getMediaItemStyle, 
  getMediaGroupId,
  shouldShowMoreIndicator,
  getMoreIndicatorText
} from '../utils/mediaGroupingUtil';
import logger, { Logger } from '../../../utils/logger';
import { DownloadCloud } from 'lucide-react';
import toast from "react-hot-toast";

// Define props interface for the styled component
interface MediaGroupContainerProps {
  cols: number;
  rows: number;
}

// Styled components with stronger specificity
const MediaGroupContainer = styled.div<MediaGroupContainerProps>`
  && {
    display: grid !important;
    grid-template-columns: repeat(${props => props.cols}, 1fr) !important;
    grid-template-rows: repeat(${props => props.rows}, 1fr) !important;
    width: 100% !important;
    max-width: 400px !important;
    min-width: 280px !important;
    gap: 4px !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    position: relative !important;
    margin-bottom: 8px !important;
    background: #f8fafc !important;
    padding: 4px !important;
    
    @media (max-width: 768px) {
      max-width: 300px !important;
      min-width: 250px !important;
      gap: 3px !important;
    }
  }
`;

const MediaGroupWrapper = styled.div`
  && {
    display: flex !important;
    align-items: flex-start !important;
    gap: 16px !important;
    margin: 12px 0 !important;
    position: relative !important;
    
    @media (max-width: 768px) {
      gap: 12px !important;
      margin: 8px 0 !important;
    }
  }
`;

const MediaItemWrapper = styled.div`
  && {
    position: relative !important;
    overflow: hidden !important;
    height: 140px !important;
    border-radius: 8px !important;
    background: white !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: transform 0.2s ease !important;
    
    &:hover {
      transform: scale(1.02) !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }
    
    @media (max-width: 768px) {
      height: 120px !important;
    }
    
    /* Ensure media components fill the entire wrapper */
    & > div {
      height: 100% !important;
    }
    
    /* Make sure MessageMedia component fills height */
    & .media-wrapper,
    & .media-preview {
      height: 100% !important;
    }
  }
`;

const MoreIndicator = styled.div`
  && {
    position: absolute !important;
    bottom: 8px !important;
    right: 8px !important;
    background: rgba(0, 0, 0, 0.8) !important;
    color: white !important;
    padding: 6px 12px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    border-radius: 16px !important;
    z-index: 20 !important;
    backdrop-filter: blur(4px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
    
    @media (max-width: 768px) {
      padding: 4px 8px !important;
      font-size: 11px !important;
      bottom: 6px !important;
      right: 6px !important;
    }
  }
`;

const DownloadAllButton = styled.button`
  && {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: white !important;
    border: none !important;
    border-radius: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    flex-shrink: 0 !important;
    align-self: flex-start !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    position: relative !important;
    
    &:hover {
      background: linear-gradient(135deg, #5a67d8 0%, #667eea 100%) !important;
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4) !important;
    }

    &:active {
      transform: translateY(0px) !important;
    }
    
    &:disabled {
      background: rgba(0, 0, 0, 0.3) !important;
      cursor: not-allowed !important;
      transform: none !important;
      box-shadow: none !important;
    }
    
    @media (max-width: 768px) {
      width: 40px !important;
      height: 40px !important;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
    border-width: 1.5px;
  }
`;

interface MediaGroupProps {
  messages: any[];
}

export const MediaGroup: React.FC<MediaGroupProps> = ({ messages }) => {
  const dispatch = useAppDispatch();
  const [loadingMedia, setLoadingMedia] = useState<{[key: string]: boolean}>({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  
  // Display first 6 messages only
  const displayMessages = messages.slice(0, 6);
  const groupSize = messages.length;
  
  // Get layout configuration
  const { columns, rows } = getGridConfig(groupSize);
  
  // Generate a unique group ID
  const groupId = getMediaGroupId(messages);
  
  
  
  const handleMediaDownload = useCallback(async (message: any): Promise<string | null> => {
    if (!message._id) {
      logger.log("MediaGroup - Cannot download media - missing message ID");
      return null;
    }
    
    logger.log("MediaGroup - Downloading media for message:", message._id);
    setLoadingMedia(prev => ({ ...prev, [message._id]: true }));
    
    try {
      // Import and use the actual service
      const { downloadChatMedia } = await import("../../../services/whatsapp");
      
      let mediaId = message?.message?.id;
      if (!mediaId) {
        logger.log("MediaGroup - Media ID not found in message:", message);
        throw new Error("Media ID not found");
      }
      
      logger.log("MediaGroup - Using mediaId:", mediaId);
      let formdata = new FormData();
      formdata.append("mediaId", mediaId);
      
      const response = await downloadChatMedia(formdata);
      logger.log("MediaGroup - Download response received", { 
        hasData: !!response.data 
      });
      
      if (response.data) {
        const blob = new Blob([response.data], {
          type: message.messageType === "document"
            ? "application/pdf"
            : message.message.mime_type,
        });
        
        const url = URL.createObjectURL(blob);
        logger.log("MediaGroup - Created blob URL:", url);
        
        // Cache in Redux store
        dispatch(cacheMedia({
          messageId: message._id,
          url,
          type: message.messageType || 'unknown',
          mimeType: message.message?.mime_type || 'application/octet-stream'
        }));
        
        logger.log("MediaGroup - Media cached in Redux for:", message._id);
        return url; // Return the URL
      }
      
      return null;
    } catch (error) {
      logger.log("MediaGroup - Error downloading media:", error);
      return null;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [message._id]: false }));
    }
  }, [dispatch]);
  
  // Handle downloading all media in the group

const handleDownloadAll = useCallback(async () => {
  setIsDownloadingAll(true);
  logger.log("MediaGroup - Starting download all for group:", groupId);
  logger.log("MediaGroup - Total messages to download:", messages.length);
  logger.log("MediaGroup - Messages:", messages.map(m => ({ id: m._id, type: m.messageType, filename: m.message?.filename })));
  
  try {
    // Create an array of download promises with staggered execution
    const downloads = messages.map(async (message, index) => {
      // Add a small delay between downloads to avoid overwhelming
      await new Promise(resolve => setTimeout(resolve, index * 200));
      try {
        logger.log(`MediaGroup - Downloading item ${index + 1}/${messages.length}:`, message._id);
        // This function now returns a URL or null
        const url = await handleMediaDownload(message);
        
        // Only attempt download if URL is returned
        if (url) {
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = url;
          link.download = message.message?.filename || `${message.messageType || 'file'}-${Date.now()}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          logger.log(`MediaGroup - Successfully downloaded item ${index + 1}:`, message._id);
          return { messageId: message._id, success: true };
        }
        
        logger.log(`MediaGroup - Failed to get URL for item ${index + 1}:`, message._id);
        return { messageId: message._id, success: false };
      } catch (error) {
        logger.log(`MediaGroup - Error downloading item ${index + 1}:`, message._id, error);
        return { messageId: message._id, success: false, error };
      }
    });
    
    // Wait for all downloads to complete
    const results = await Promise.all(downloads);
    
    const successful = results.filter(r => r.success).length;
    logger.log(`MediaGroup - Downloaded ${successful} of ${messages.length} items`);
    
    if (successful === messages.length) {
      toast.success(`Downloaded all ${messages.length} media items`);
    } else {
      toast.success(`Downloaded ${successful} of ${messages.length} media items`);
    }
  } catch (error) {
    logger.log("MediaGroup - Error in download all:", error);
    toast.error("Failed to download some media items");
  } finally {
    setIsDownloadingAll(false);
  }
}, [groupId, messages, handleMediaDownload]);
  
  // Handle preview with group awareness
  const handlePreview = useCallback((messageId: string) => {
    // Open the preview with group context
    dispatch(openMediaPreview(messageId));
    
    // Set group context in Redux for navigation
    dispatch({
      type: 'media/setPreviewGroupContext',
      payload: {
        groupId,
        messageIds: messages.map(msg => msg._id)
      }
    });
  }, [dispatch, groupId, messages]);
  
  // Debugging - log what we're rendering
  logger.log(`MediaGroup - Rendering grid with ${columns} columns, ${rows} rows and ${displayMessages.length} items`);
  
  return (
   
  <MediaGroupWrapper>
    <div style={{ position: 'relative' }}>
      <MediaGroupContainer cols={columns} rows={rows}>
        {displayMessages.map((message, index) => {
         
          
          return (
            <MediaItemWrapper key={message._id}>
            <MessageMedia
              message={message}
              onDownload={async () => {
                // Make sure we return the URL from handleMediaDownload
                return handleMediaDownload(message);
              }}
              onPreview={() => handlePreview(message._id)}
              integrationInfo={{}}
              thumbnailMode={true}
              isInGroup={true}
            />
          </MediaItemWrapper>
          );
        })}
      </MediaGroupContainer>
      
      {shouldShowMoreIndicator(groupSize) && (
        <MoreIndicator>
          {getMoreIndicatorText(groupSize)}
        </MoreIndicator>
      )}
    </div>
    
    <DownloadAllButton 
      onClick={handleDownloadAll}
      disabled={isDownloadingAll}
      title="Download all media"
    >
      {isDownloadingAll ? (
        <LoadingSpinner />
      ) : (
        <DownloadCloud size={16} />
      )}
    </DownloadAllButton>
  </MediaGroupWrapper>
);

};

export default MediaGroup;